/**
 * Where the touch controls are, in one place.
 *
 * Both halves of a virtual control need this: the input source has to decide
 * which region a finger landed in, and the overlay has to draw the ring the
 * finger is aiming at. Two copies of that arithmetic agree on the day they are
 * written and disagree on some later one, and the failure is a control that
 * looks like it is under your thumb and does not respond — which reads as a
 * broken game rather than as a layout bug. The radar makes the same argument for
 * `scopeOffset`: one mapping, so the two cannot disagree.
 *
 * Pure, and takes the viewport and the safe-area insets as arguments, so it is
 * unit-testable without a browser and without `matchMedia`.
 */

/** A circular control: centre and radius, in CSS pixels from the top left. */
export interface TouchDisc {
  cx: number;
  cy: number;
  radius: number;
}

export interface TouchLayout {
  /** The movement stick's outer ring, and the knob that rides inside it. */
  stick: TouchDisc & { knobRadius: number };
  /** The fire circle. */
  fire: TouchDisc;
  /**
   * How far the knob may travel from the ring's centre before it clamps.
   *
   * Deliberately *less* than the ring radius, so the knob stays inside the ring
   * it is drawn in rather than sitting half outside at full deflection.
   */
  stickTravel: number;
}

/**
 * A tap target is never smaller than this.
 *
 * 44 CSS px is Apple's HIG and Material's floor, and `CLAUDE.md` requires it
 * project-wide. The stick and the fire circle are both comfortably above it at
 * every viewport this clamps to; the constant is here so that a future attempt
 * to shrink them for a small screen fails a test rather than shipping a control
 * a thumb misses.
 */
export const MIN_TAP_TARGET = 44;

/** Ring diameter as a fraction of the shorter viewport edge, and its bounds. */
const STICK_FRACTION = 0.22;
const STICK_DIAMETER_MIN = 112;
const STICK_DIAMETER_MAX = 168;

const FIRE_FRACTION = 0.15;
const FIRE_DIAMETER_MIN = 76;
const FIRE_DIAMETER_MAX = 112;

/** The knob, as a fraction of the ring. Matches the reference image's proportions. */
const KNOB_RATIO = 0.4;
/**
 * Travel, as a fraction of the ring.
 *
 * **Bounded by `1 - KNOB_RATIO`, and that is arithmetic rather than taste**: the
 * knob is drawn centred on its offset, so travel plus the knob's own radius is
 * how far its *edge* reaches. At the 0.62 this started as, the sum was 1.02 and
 * the knob hung two per cent outside the ring it belongs to at full deflection —
 * small enough to look like a rendering artifact and not like a wrong constant.
 * A unit test now pins the sum rather than the value, so retuning either ratio
 * cannot reintroduce it.
 */
const TRAVEL_RATIO = 0.56;

/** Gap from the safe area to the control's edge. */
const EDGE_MARGIN = 22;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** A usable number, or a fallback big enough that the clamps below decide the size. */
const finite = (v: number): number => (Number.isFinite(v) && v > 0 ? v : Number.POSITIVE_INFINITY);

export interface Insets {
  left: number;
  right: number;
  bottom: number;
}

export const NO_INSETS: Insets = { left: 0, right: 0, bottom: 0 };

/**
 * Places both controls for a viewport.
 *
 * Sized from the **shorter** edge, which in the landscape this game locks to is
 * the height. A width-derived size would grow the stick on a wide phone, where
 * the constraint is actually how far a thumb reaches up from the bottom edge.
 */
export function touchLayout(
  viewport: { width: number; height: number },
  insets: Insets = NO_INSETS,
): TouchLayout {
  // `Math.min` propagates NaN and `Math.max` does not clear it, so a viewport
  // that has not been measured yet — which is every viewport for one frame,
  // before `attach` runs — would size every control to NaN and place them
  // nowhere. Coerced rather than thrown on: a control drawn at a default size is
  // recoverable on the next resize, and an exception here takes the arena's
  // whole HUD payload down with it.
  const shorter = Math.max(1, Math.min(finite(viewport.width), finite(viewport.height)));

  const stickRadius =
    clamp(shorter * STICK_FRACTION, STICK_DIAMETER_MIN, STICK_DIAMETER_MAX) / 2;
  const fireRadius = clamp(shorter * FIRE_FRACTION, FIRE_DIAMETER_MIN, FIRE_DIAMETER_MAX) / 2;

  return {
    stick: {
      cx: insets.left + EDGE_MARGIN + stickRadius,
      cy: viewport.height - insets.bottom - EDGE_MARGIN - stickRadius,
      radius: stickRadius,
      knobRadius: stickRadius * KNOB_RATIO,
    },
    fire: {
      cx: viewport.width - insets.right - EDGE_MARGIN - fireRadius,
      cy: viewport.height - insets.bottom - EDGE_MARGIN - fireRadius,
      radius: fireRadius,
    },
    stickTravel: stickRadius * TRAVEL_RATIO,
  };
}

/**
 * How generously a control catches a finger.
 *
 * Bigger than what is drawn, on purpose: a thumb's contact patch is centimetres
 * across and its reported point drifts toward the base of the thumb, so a
 * hit-test exactly on the visible ring loses touches that looked well inside it.
 * Erring the other way costs nothing here — the two controls are in opposite
 * corners, and the view drag deliberately ignores whatever the controls claim.
 */
export const HIT_SLOP = 1.35;

export function withinDisc(disc: TouchDisc, x: number, y: number, slop = HIT_SLOP): boolean {
  const dx = x - disc.cx;
  const dy = y - disc.cy;
  const r = disc.radius * slop;
  return dx * dx + dy * dy <= r * r;
}

/**
 * The stick's deflection as two axes in [-1, 1], clamped to a circle.
 *
 * Circular rather than square clamping so a diagonal push is not worth √2 times
 * a straight one. It changes where the knob is *drawn* — a knob that leaves its
 * ring on the diagonal reads as a bug — and, now that the axes downstream are
 * continuous, it changes the packet too: without it a diagonal push would report
 * a magnitude of 1.41 and `stickAxes` would read every corner of the square as
 * fully committed.
 */
export function stickVector(
  layout: TouchLayout,
  x: number,
  y: number,
): { x: number; y: number } {
  const dx = x - layout.stick.cx;
  const dy = y - layout.stick.cy;
  const distance = Math.hypot(dx, dy);
  if (distance <= 1e-6) return { x: 0, y: 0 };
  const scale = Math.min(1, distance / layout.stickTravel) / distance;
  return { x: dx * scale, y: dy * scale };
}

/**
 * How far the stick must leave centre before it drives anything, as a fraction
 * of full travel.
 *
 * **Radial, and much smaller than the 0.28 it replaces.** That figure was the
 * *whole* of an analogue-to-discrete conversion: the wire carried a ternary axis,
 * so a deadzone was the only tuning there was, and it had to be wide enough that
 * a resting thumb did not slam the tank to full speed. The wire is bounded rather
 * than ternary now (`InputCommand.forward`), so this has one job left — a thumb
 * resting on the stick must not drive — and 0.18 of travel is about 3 mm on a
 * phone, comfortably past a contact patch's wander and 10% more of the stick
 * given back to steering.
 *
 * Radial rather than per axis, and that is the difference a player feels. A
 * per-axis deadzone puts a dead cross through the middle of the stick: a push
 * 15° off vertical produces `x = 0.26`, under the old threshold, so a gentle
 * lean steered not at all and then jumped. Testing the *magnitude* leaves the
 * direction fully expressive the instant the stick is out of the well.
 */
export const STICK_DEADZONE = 0.18;

/**
 * How close to an axis counts as fully committed to it, in degrees.
 *
 * Asked for in exactly these terms — *"100% zone starts at about last 20deg to
 * horizontal, same with speed"* — and it is the right shape for a thumb: the
 * corners of a round stick are hard to hold, so demanding a perfectly horizontal
 * push for a full-rate turn means never getting one. Inside 20° of an axis, that
 * axis reads full.
 *
 * The two axes share the figure. Turn saturates within 20° of horizontal, speed
 * within 20° of vertical, so the two saturation cones do not overlap (20 + 20 <
 * 90) and there is no stick direction that reads full on both.
 */
export const STICK_SATURATION_DEGREES = 20;

/** `cos(20°)`. A component at or past this is the whole of its axis. */
const SATURATION = Math.cos((STICK_SATURATION_DEGREES * Math.PI) / 180);

/**
 * The stick's deflection as the two axes the wire carries, in [-1, 1].
 *
 * Three steps, and each of them is a thing a player asked for:
 *
 * **Direction decides the split.** The deflection is normalised, so the ratio
 * between turning and driving is the angle the thumb is pushing at and nothing
 * else — a push 10° off vertical is 10° of steering, where before it was a
 * full-rate turn (*"a slight turn effects at 100% turn speed"*).
 *
 * **The last 20° to an axis is the whole of it.** Each component is divided by
 * `cos(20°)` and clamped, so a push within 20° of horizontal turns at the full
 * hull rate and one within 20° of vertical drives at top speed. Without it the
 * ends of the range would be unreachable in practice: full speed would need a
 * thumb held exactly on the vertical.
 *
 * **Magnitude scales both.** A half-pushed stick is half throttle and half turn
 * (*"allow gradual speed with joystick"*), measured from the deadzone rather than
 * from the centre so the usable travel starts where the stick starts responding
 * — otherwise the first thing past the deadzone would be a jump to 18%.
 *
 * Pure and takes the already-clamped deflection, so it is unit-testable without a
 * browser and cannot disagree with the knob the overlay draws — that comes from
 * the same `stickVector`.
 */
export function stickAxes(deflection: { x: number; y: number }): {
  forward: number;
  turn: number;
} {
  const magnitude = Math.hypot(deflection.x, deflection.y);
  if (magnitude <= STICK_DEADZONE) return { forward: 0, turn: 0 };

  const strength = Math.min(1, (magnitude - STICK_DEADZONE) / (1 - STICK_DEADZONE));
  const saturate = (component: number): number => {
    const scaled = component / magnitude / SATURATION;
    return Math.max(-1, Math.min(1, scaled));
  };

  return {
    // Screen y grows downward and pushing the stick *up* is forward, so the sign
    // flips here. Getting it wrong is a tank that reverses when told to advance,
    // which is obvious — unlike the turn axis, which is merely mirrored.
    forward: saturate(-deflection.y) * strength,
    turn: saturate(deflection.x) * strength,
  };
}
